import { jsx } from 'react/jsx-runtime';
import { styled } from '@flex-design-system/stitches-react';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as HoverCard from '@radix-ui/react-hover-card';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import classNames from 'classnames';
import { forwardRef } from 'react';
import usePopperPresence from '../../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../../hooks/usePopperZIndexStyle.mjs';
import { usePopoverContext, PopoverProvider } from '../context.mjs';
import { DEFAULT_COLLISION_PADDING } from '../shared/constants.mjs';
import { contentStyle, contentInnerStyle } from '../shared/styles.vanilla.mjs';
import { content } from './hoverPopover.vanilla.mjs';

const HoverPopover = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  openDelay = 0,
  closeDelay = 0,
  onClose,
  ...props
}) => {
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const { ref: presenceRef } = usePopperPresence({
    open,
    onPresenceChange: (isPresent) => !isPresent && onClose?.()
  });
  return /* @__PURE__ */ jsx(PopoverProvider, { open, presenceRef, children: /* @__PURE__ */ jsx(
    HoverCard.Root,
    {
      open,
      onOpenChange: setOpen,
      openDelay,
      closeDelay,
      ...props
    }
  ) });
};
const PopoverContent = forwardRef(
  ({
    className: classNameFromProps,
    style: styleFromProps,
    sideOffset = 4,
    zIndex,
    collisionPadding = DEFAULT_COLLISION_PADDING,
    ...props
  }, forwardedRef) => {
    const { open, presenceRef } = usePopoverContext("HoverPopover.Content");
    const composedRefs = useComposedRefs(presenceRef, forwardedRef);
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    const className = classNames(
      contentStyle,
      contentInnerStyle,
      content,
      classNameFromProps
    );
    const style = Object.assign({}, zIndexStyle, styleFromProps);
    return /* @__PURE__ */ jsx(HoverCard.Portal, { children: /* @__PURE__ */ jsx(
      StyledContent,
      {
        ...props,
        className,
        style,
        sideOffset,
        ref: composedRefs,
        collisionPadding
      }
    ) });
  }
);
const StyledContent = styled(HoverCard.Content);
HoverPopover.Trigger = HoverCard.Trigger;
HoverPopover.Content = PopoverContent;

export { HoverPopover };
//# sourceMappingURL=HoverPopover.mjs.map
